import React from 'react';
import { motion } from 'framer-motion';
import './css/BusinessVertical.css';
import SEO from '../components/SEO';
import { serviceSchema, productSchema } from '../components/StructuredData';

const GreenMetalWorking = () => {
  const structuredData = [
    serviceSchema(
      "Green Metal Working Additives & Lubricants",
      "Biodegradable lubricity additives, boundary lubricants, and corrosion inhibitors for synthetic and semi-synthetic metalworking fluids.",
      "Metal Working Solutions"
    ),
    productSchema(
      "Green Metal Working Fluids",
      "Mineral oil-free, biodegradable metalworking fluid additives for sustainable manufacturing",
      "Green Metal Working"
    )
  ];

  return (
    <>
      <SEO
        title="Green Metal Working Additives - Sustainable Lubricants | Nimble Group"
        description="Leading manufacturer of biodegradable metal working additives and lubricants. 100% mineral oil-free solutions with 60% reduced health risk for 3.4M+ workers protected worldwide."
        keywords="Metalworking Fluid Additive Manufacturer, Coolant Additive Supplier, Eco friendly cutting oil, Synthetic Coolant Additive, Semi-Synthetic Coolant Concentrates, MWF Additives, Water-Based Metalworking Fluid Ingredients, Eco friendly coolants, Amine free metal  working fluids, Oil free coolants, Mineral oil free coolants, Corrosion inhibitor for Hydro testing, Amine free coolant, Boron-Free Coolant Additives, Phenol-Free Additives, EU Ecolabel Lubricant Additives, Sulphur free lubricity additive, Sustainable Metalworking Fluid Additives, High-Performance MWF Additives, Long Sump Life Coolant Additives, Extreme Pressure Additive Manufacturers, Lubricity Additive for Coolant, Ester Lubricity Additives MWF, Oil free cutting oil, Amine free rust inhibitor, Amine free corrosion inhibitor, green metal working, biodegradable lubricants, metal working fluids, sustainable lubricants, mineral oil-free, boundary lubricants, corrosion inhibitors"
        canonical="/business/green-metal-working"
        structuredData={structuredData}
      />
      <GreenMetalWorkingContent />
    </>
  );
};

const GreenMetalWorkingContent = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Key stats with animation
  const stats = [
    { value: '0%', label: 'Mineral Oil' },
    { value: '100%', label: 'Biodegradable' },
    { value: '60%', label: 'Reduced Health Risk' },
    { value: '3.4M', label: 'Workers Protected' }
  ];

  return (
    <div className="business-vertical-page">
      <motion.div 
        className="business-vertical-hero green-metal-working"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h1>Green Metal Working Additives</h1>
          <h2 className="tagline">"Empowering modern metal working fluids for Sustainability & cost optimization."</h2>
        </div>
      </motion.div>

      <section className="business-vertical-content">
        <div className="container">
          <motion.div
            className="stats-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                className="stat-box"
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={fadeIn.transition}
          >
            <h3>Overview</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🌱</span>
              </div>
              <div className="section-content">
                <p>Our green chemistry-based additives enhance the performance and sustainability of metal working fluids, cutting oils, and synthetic coolants. Designed for biodegradability, superior lubricity, and corrosion protection, our additives support industries moving towards eco-compliant, efficient operations.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
          >
            <h3>Applications</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🔧</span>
              </div>
              <div className="section-content">
                <ul>
                  <li>Advanced mineral oil free metal working fluids</li>
                  <li>Amine free corrosion inhibitors</li>
                  <li>Sulphur & phosphorous free EP agents</li>
                  <li>Lubricity additives for synthetic & semi-synthetic cutting oils</li>
                  <li>Hydro-testing additives for pipe and vessel integrity testing</li>
                  <li>Eco friendly Corrosion inhibitors for coolant systems</li>
                  <li>Bio-based boundary layer lubricants for modern CNC machining</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
          >
            <h3>Why Green Metal Working Additives?</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🔍</span>
              </div>
              <div className="section-content">
                <p>Metalworking fluids (MWFs) are essential for metalworking operations, but they can also have a significant negative impact on the environment, health, and safety (EHS) of workers and the surrounding community.</p>
                <p>The identity and proportion of chemical species in traditional MWFs is dependent on a number of factors including the manufacturer and the cooling and lubrication requirements of the machining process. Moreover, a number of additives such as biocides and anti-foaming agents are added to the metal working fluids to enhance their operational lifespan.</p>
                <p>Of considerable concern, an unknown number and amount of contaminants become part of the complex mixture during machining operations. These contaminants can include tramp oils, reaction products such as nitrosamines, and microbial agents such as bacteria and fungi.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
          >
            <h3>Health & Safety Concerns</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">⚕️</span>
              </div>
              <div className="section-content">
                <p>Workers can be exposed to metal working fluids through skin contact by exposure to splashes during immersion or flooding of the machine tool or work, and handling parts, tools, and equipment covered with metal working fluids. Workers also receive a significant exposure to metal working fluids by inhalation of aerosols/mist.</p>
                <p>The National Occupational Exposure Survey lists an estimated 3.4 million workers who are potentially exposed to agents collectively called metalworking fluids.</p>
                <p>A wide variety of chemicals may be used in each of the metal working fluid classes, and the risk these chemicals pose to workers may vary because of different manufacturing processes, various degrees of refining, recycling, improperly reclaimed chemicals, different degrees of chemical purity, and potential chemical reactions between components.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.4 }}
          >
            <h3>Environmental Impact of Traditional MWFs</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🌍</span>
              </div>
              <div className="section-content">
                <h4>Soil Pollution</h4>
                <p>MWFs and cutting oils can leak or spill into the soil, contaminating it with heavy metals, hydrocarbons, and other toxic chemicals. This can damage soil ecosystems and reduce crop yields.</p>
                
                <h4>Water Pollution</h4>
                <p>MWFs and cutting oils can enter waterways through storm drains or wastewater treatment plants. This can pollute drinking water sources and harm aquatic life.</p>
                
                <h4>Air Pollution</h4>
                <p>MWFs and cutting oils can volatilize into the air, creating an aerosol mist. This mist can contain harmful chemicals that can be inhaled by workers and residents living near metalworking facilities.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.5 }}
          >
            <h3>Harmful Components in Traditional MWFs</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">⚠️</span>
              </div>
              <div className="section-content">
                <ul>
                  <li><strong>Amines:</strong> Used as corrosion inhibitors and biocides, but toxic to aquatic life and can pollute drinking water sources.</li>
                  <li><strong>Chlorines:</strong> Used as biocides and extreme pressure lubricants, but toxic to aquatic life and can form harmful compounds in the environment.</li>
                  <li><strong>Nitrates:</strong> Used as corrosion inhibitors, but can pollute drinking water sources and contribute to algal blooms in waterways.</li>
                  <li><strong>Sulfur:</strong> Used as an extreme pressure lubricant, but can form harmful compounds in the environment, such as sulfur dioxide and sulfuric acid.</li>
                  <li><strong>Mineral oil:</strong> Base oil in many MWFs, but toxic to aquatic life and can pollute drinking water sources.</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.6 }}
          >
            <h3>Health Risks of Traditional MWFs</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🩺</span>
              </div>
              <div className="section-content">
                <p>Exposure to traditional MWFs can cause various health problems, including:</p>
                <ul>
                  <li>Skin irritation and dermatitis</li>
                  <li>Eye irritation and conjunctivitis</li>
                  <li>Respiratory problems such as asthma, bronchitis, and pneumonia</li>
                  <li>Liver damage</li>
                  <li>Kidney damage</li>
                  <li>Cancer</li>
                </ul>
                <p>Long-term exposure can increase the risk of developing certain types of cancer, such as lung cancer, bladder cancer, and skin cancer. MWFs can also cause reproductive problems, such as infertility and miscarriage.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.7 }}
          >
            <h3>Our Green Alternative Solution</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">💚</span>
              </div>
              <div className="section-content">
                <p>Our green metal working additives provide a safer, environmentally friendly alternative to traditional MWFs by:</p>
                <ul>
                  <li>Eliminating hazardous chemicals such as amines, chlorines, nitrates, and sulfur</li>
                  <li>Offering mineral oil-free formulations</li>
                  <li>Providing biodegradable alternatives that don't persist in the environment</li>
                  <li>Reducing VOC emissions and associated respiratory risks</li>
                  <li>Maintaining or improving performance compared to traditional products</li>
                  <li>Supporting compliance with increasingly strict environmental regulations</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.8 }}
          >
            <h3>Benefits of Our Green Metal Working Additives</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">✅</span>
              </div>
              <div className="section-content">
                <h4>Environmental Benefits</h4>
                <ul>
                  <li>Lower environmental footprint</li>
                  <li>Reduced soil and water contamination</li>
                  <li>Decreased air pollution and VOC emissions</li>
                  <li>Biodegradable formulations</li>
                </ul>
                
                <h4>Health Benefits</h4>
                <ul>
                  <li>Reduced skin and respiratory irritation</li>
                  <li>Lower risk of long-term health effects</li>
                  <li>Improved workplace safety</li>
                  <li>Enhanced worker comfort and satisfaction</li>
                </ul>
                
                <h4>Business Benefits</h4>
                <ul>
                  <li>Compliance with environmental regulations</li>
                  <li>Reduced waste disposal costs</li>
                  <li>Improved corporate sustainability profile</li>
                  <li>Enhanced reputation with customers and community</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="cta-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
          >
            <h3>Ready to Transform Your Metal Working Operations?</h3>
            <p>Contact our team to discover how our green metal working additives can improve safety, sustainability, and performance in your manufacturing processes.</p>
            <button className="cta-button">Request Product Information</button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GreenMetalWorking; 